import type { ClientOverview, Currency } from '../../types/api';
import { convertUSDToCurrency, convertUSDToCurrencyFormatted } from './currencyConversion';

/**
 * Calculate total net annual rent yield USD for a client across all units
 * @param clientOverviewData - Array of ClientOverview data for all units
 * @returns Total net annual rent yield USD value
 */
export function calculateNetAnnualRentYieldUSD(clientOverviewData: ClientOverview[]): number {
  if (!clientOverviewData || clientOverviewData.length === 0) {
    return 0;
  }

  return clientOverviewData.reduce((total, overview) => {
    return total + (overview.net_annual_rent_yield_usd || 0);
  }, 0);
}

/**
 * Calculate total net annual rent yield USD with formatting
 * @param clientOverviewData - Array of ClientOverview data for all units
 * @returns Formatted total net annual rent yield USD string
 */
export function calculateNetAnnualRentYieldUSDFormatted(clientOverviewData: ClientOverview[]): string {
  const total = calculateNetAnnualRentYieldUSD(clientOverviewData);
  
  if (total === 0) {
    return '0';
  }
  
  // Format with commas for thousands
  return total.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  });
}

/**
 * Calculate total net annual rent yield in a specific currency
 * @param clientOverviewData - Array of ClientOverview data for all units
 * @param targetCurrencyCode - Target currency code
 * @param currencies - Array of currency data
 * @returns Total net annual rent yield value in target currency
 */
export function calculateNetAnnualRentYieldInCurrency(
  clientOverviewData: ClientOverview[],
  targetCurrencyCode: string,
  currencies: Currency[]
): number {
  const totalUSD = calculateNetAnnualRentYieldUSD(clientOverviewData);
  return convertUSDToCurrency(totalUSD, targetCurrencyCode, currencies);
}

/**
 * Calculate total net annual rent yield in a specific currency with formatting
 * @param clientOverviewData - Array of ClientOverview data for all units
 * @param targetCurrencyCode - Target currency code
 * @param currencies - Array of currency data
 * @returns Formatted total net annual rent yield string in target currency
 */
export function calculateNetAnnualRentYieldInCurrencyFormatted(
  clientOverviewData: ClientOverview[],
  targetCurrencyCode: string,
  currencies: Currency[]
): string {
  const totalUSD = calculateNetAnnualRentYieldUSD(clientOverviewData);
  return convertUSDToCurrencyFormatted(totalUSD, targetCurrencyCode, currencies, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
    useCurrencySymbol: false
  });
}












